import type { ChecklistItem, ChecklistItemInput } from '@amumin/api-client';

// Checklist bij een herinnering (0031, golf 2; founder #7: "bij zwemles, Onderbroek, Sokken,
// Handdoek, etc."). Conceptvorm in het formulier, omzetten van/naar de API en de voorstellen per
// activiteit. De namen van de voorstellen staan in i18n (`a.checklist.items`), met als sleutel het
// pictogram: zo heeft elk voorstel een plaatje dat een kind dat nog niet leest herkent.

export const CHECKLIST_MAX = 12;
export const CHECKLIST_LABEL_MAX = 40;
export const CHECKLIST_DEFAULT_ICON = 'backpack';

export interface ChecklistDraft {
  /** Bestaand ding (vinkjes blijven staan); ontbreekt = nieuw. */
  id?: string;
  /** Lokale sleutel voor de lijst. */
  key: string;
  label: string;
  icon: string;
}

let seq = 0;
export function checklistKey(): string {
  seq += 1;
  return `c${Date.now().toString(36)}${seq}`;
}

export function checklistFromItems(
  items: ReadonlyArray<ChecklistItem> | undefined,
): ChecklistDraft[] {
  return (items ?? []).map((i) => ({ id: i.id, key: i.id, label: i.label, icon: i.icon }));
}

export function checklistInput(list: ReadonlyArray<ChecklistDraft>): ChecklistItemInput[] {
  return list.map((i) => ({
    ...(i.id ? { id: i.id } : {}),
    label: i.label.trim(),
    icon: i.icon || CHECKLIST_DEFAULT_ICON,
  }));
}

function norm(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/**
 * Voorstellen voor de checklist op basis van de afspraak (en de tekst van de herinnering):
 * zwemles → zwembroek/badpak, handdoek, onderbroek, sokken, shampoo, slippers; voetbal →
 * voetbalschoenen, scheenbeschermers, sokken, bidon; moskeeles/koranles → Koran, tas,
 * hoofddoek, takke; school → tas, lunch, fruit, bidon, huiswerk. Pictogram-id's (= sleutels van
 * `a.checklist.items`), zonder wat al op de lijst staat.
 */
export function suggestChecklist(
  event: { title: string; icon: string | null; category: string | null },
  reminderTitle: string,
  have: ReadonlyArray<ChecklistDraft> = [],
): string[] {
  const text = norm(`${event.title} ${reminderTitle}`);
  const icon = event.icon ?? '';
  const out: string[] = [];
  const add = (...ids: string[]) => {
    for (const id of ids) if (out.indexOf(id) < 0) out.push(id);
  };
  if (/zwem/.test(text) || icon === 'swimming' || icon === 'swim') {
    add('swim-trunks', 'swimsuit', 'towel', 'underwear', 'socks', 'shampoo', 'slippers');
  }
  if (/(voetbal|football|soccer)/.test(text) || icon === 'soccer') {
    add('football-boots', 'shin-guards', 'socks', 'water-bottle');
  }
  if (/hockey/.test(text) || icon === 'hockey') {
    add('shin-guards', 'shoes', 'water-bottle');
  }
  if (
    /(training|trainen|judo|turnen|gym|dans|tennis|basketbal|korfbal|sport)/.test(text) ||
    ['sport', 'judo', 'gymnastics', 'dance', 'ballet', 'tennis', 'basketball'].indexOf(icon) >= 0 ||
    event.category === 'sport'
  ) {
    add('get-dressed', 'shoes', 'water-bottle', 'towel');
  }
  if (
    /(moskee|koran|quran|camii|kuran|arabisch)/.test(text) ||
    icon === 'mosque' ||
    icon === 'quran'
  ) {
    add('quran', 'backpack', 'headscarf', 'prayer-cap');
  }
  if (/(school|bijles)/.test(text) || icon === 'school' || event.category === 'school') {
    add('backpack', 'lunch', 'fruit', 'water-bottle', 'homework');
  }
  if (/(muziek|piano|gitaar|viool)/.test(text) || icon === 'music-lesson' || icon === 'piano') {
    add('music-lesson', 'backpack');
  }
  if (out.length === 0) add('backpack', 'water-bottle', 'coat');
  const icons = have.map((i) => i.icon);
  return out.filter((id) => icons.indexOf(id) < 0);
}
